import type { Messages } from "@prop/i18n";
import type en from "../en/coach";

export default {
  nav: "Coach",
  title: "Coach",
  lead: "De dónde vienen tus resultados, un mentor que lee tus operaciones y el camino hacia una cuenta funded.",
  account: "Cuenta",
  tabs: { analytics: "Mapa de ventaja", mentor: "Mentor", playbook: "Playbook", academy: "Academia", path: "Camino a funded" },
  day: { warning: "Cuidado: estás cerca de tu límite", locked: "Bloqueado por hoy", reasons: { green_to_red: "el día devolvió demasiado desde su máximo", personal_daily_loss: "tu propio límite de pérdida diaria" } },
  guard: {
    codes: {
      COACH_PAUSE_AFTER_LOSS: "pausa tras una pérdida",
      COACH_REENTRY_LIMIT: "límite de reentradas",
      COACH_TRADE_LIMIT: "operaciones por día",
      COACH_GREEN_TO_RED: "de verde a rojo",
      COACH_OUTSIDE_WINDOW: "fuera de tu horario",
      COACH_PERSONAL_DAILY_LOSS: "tu pérdida diaria",
    },
  },
  analytics: {
    dimension: "Agrupar por",
    dims: { instrument: "Instrumento", side: "Lado", hour: "Hora", weekday: "Día de la semana", holding: "Tiempo de tenencia", setup: "Setup" },
    trades: "Operaciones",
    net: "Neto",
    winRate: "Tasa de acierto",
    pf: "Profit factor",
    payoff: "Payoff",
    dd: "Drawdown máximo",
    netTotal: "Resultado neto, todas las operaciones cerradas",
    rowLine: "{trades} operaciones · acierto {win} · PF {pf}",
    emptyTitle: "Aún no hay operaciones cerradas",
    empty: "Cierra algunas operaciones y el mapa mostrará qué instrumentos, horas y setups generan tus resultados.",
    toTerminal: "Abrir la terminal",
    zone: "Horas en {tz}",
  },
  mentor: {
    intro: "El mentor lee tus operaciones, reglas y límites, nunca da consejos de mercado. Sus respuestas son coaching, no asesoramiento de inversión.",
    brief: "Briefing antes de la sesión",
    debrief: "Debriefing después de la sesión",
    weekly: "Revisión semanal",
    off: "El mentor aún no está habilitado para tu cuenta.",
    thinking: "El mentor está leyendo tus operaciones…",
    none: "Aún no hay sesiones. Pide un briefing antes de operar o un debriefing después.",
    memory: "Memoria",
    memoryLead: "Lo que el mentor tiene presente: tus compromisos, notas y datos. Marca un compromiso cuando lo cumplas.",
    kind: "Tipo",
    text: "Texto",
    placeholders: { commitment: "p. ej. No operar en los primeros 15 minutos", note: "p. ej. Hoy dormí poco", fact: "p. ej. Opero mejor en la apertura de Londres" },
    add: "Agregar",
    kinds: { note: "Nota", fact: "Dato", commitment: "Compromiso" },
  },
  playbook: {
    new: "Nuevo setup",
    intro: "Ponle nombre a tus setups; etiqueta con ellos las operaciones cerradas para ver los resultados de cada setup en el mapa de ventaja.",
    name: "Nombre del setup",
    tag: "Etiqueta",
    tagHint: "Letras minúsculas, dígitos y guiones; se completa a partir del nombre.",
    rules: "Reglas de entrada y salida",
    add: "Guardar setup",
    saved: "Setup guardado.",
    list: "Tus setups",
    none: "Aún no hay setups. Los que guardes aparecerán aquí.",
    tagTrade: "Setup",
    followed: "Seguí el plan",
  },
  academy: {
    title: "Lecciones",
    progress: "{done} de {total} leídas",
    done: "Marcar como leída",
    completed: "Leída",
    lessons: {
      "risk-of-ruin": { title: "Riesgo de ruina", body: "Arriesgando 1 % por operación, diez pérdidas seguidas cuestan cerca del 10 %. Arriesgando 5 %, cuestan el 40 %. Calcula el tamaño de cada operación a partir de su stop, no de cuán seguro te sientes." },
      "daily-loss": { title: "El límite de pérdida diaria", body: "El límite se mide sobre la equity, incluidas las pérdidas abiertas, desde el corte de día del programa. Deja margen: detente mucho antes de llegar a él, no justo en él." },
      "position-sizing": { title: "Tamaño de la posición", body: "Cantidad = riesgo en $ ÷ (distancia al stop × valor de un punto). El ticket de orden lo hace por ti con “tamaño por riesgo”." },
      tilt: { title: "Tilt", body: "Después de una pérdida, las ganas de recuperarla son más fuertes. Aléjate unos minutos y luego calcula el tamaño de la siguiente operación a partir de su stop, no de la pérdida." },
      journaling: { title: "Diario de trading", body: "Etiqueta cada operación con su setup y con si seguiste el plan. En un mes, el mapa de ventaja te muestra qué conservar y qué dejar." },
      "execution-policy": { title: "Cómo se ejecutan las órdenes aquí", body: "Las órdenes se ejecutan al instante, en 10–30 ms sin latencia añadida, recorriendo el libro real; las órdenes límite hacen cola detrás del tamaño visible. Cada ejecución guarda una instantánea: abre “Por qué aquí” en el panel de órdenes." },
    },
  },
  path: {
    intro: "Tu camino, paso a paso.",
    steps: { challenge: "Empieza un challenge", first_trade: "Primera operación", phase_passed: "Aprueba una fase", kyc: "Verifica tu identidad", funded: "Cuenta funded", first_payout: "Primer payout" },
    actions: { challenge: "Abrir una cuenta", first_trade: "Abrir la terminal", phase_passed: "Abrir la terminal", kyc: "Verificar ahora", funded: "Ver tus cuentas", first_payout: "Ir a payouts" },
    badges: "Insignias",
    earned: "{n} de {total}",
    achievements: {
      first_trade: "Primera operación",
      ten_trades: "Diez operaciones",
      green_day: "Día en verde",
      plan_followed_5: "Seguiste el plan 5 veces",
      academy_done: "Academia completada",
      phase_passed: "Fase aprobada",
      funded: "Funded",
      first_payout: "Primer payout",
    },
  },
  admin: {
    tab: "Experimentos",
    flag: "Flag",
    enabled: "Activo",
    rollout: "% de tratamiento",
    save: "Guardar",
    variant: "Variante",
    users: "Usuarios",
    pF: "Tasa de aprobación p_f",
    q: "Funded con pago q",
    dll: "Incumplimientos de DLL",
    retention: "Retención a 7 días",
  },
} satisfies Messages<typeof en>;
